<template>
    <v-container class="mt-12 pt-8" style="background:#fafafa;">
        <!-- Filter Row -->
        <v-row class="align-center justify-center">
            <v-col cols="12" md="3" class="py-0 d-flex justify-center mx-5 my-2">
                <v-text-field :label="i18n['uuid']" outlined v-model="filters.uuid" :loading="loading" dense
                    hide-details :class="localizedClass()" />
            </v-col>

            <v-col cols="12" md="3" class="py-0 d-flex justify-center mx-5 my-2">
                <v-text-field :label="i18n['date']" type="date" outlined v-model="filters.date" dense hide-details
                    :class="localizedClass()" />
            </v-col>

            <v-col cols="12" md="2" class="py-0 d-flex justify-center">
                <v-btn color="secondary" block :loading="loading" @click="trackOrders" :class="localizedClass()">
                    {{ i18n["track"] }}
                    <v-icon right size="15">search</v-icon>
                </v-btn>
            </v-col>
            <v-col cols="12" md="2" class="py-0 d-flex justify-center">
                <v-btn color="primary" class="mt-4" @click="exportLeadUpdates" :loading="loading">
                    {{ i18n["export"] || "Export" }}
                    <v-icon right>mdi-download</v-icon>
                </v-btn>
            </v-col>

        </v-row>

        <!-- Results Table -->
        <v-row v-if="dataTracking.length > 0" class="justify-center mt-10">

            <v-col cols="12" md="10">
                <v-simple-table>
                    <thead>
                        <tr>
                            <th>#</th>
                            <th>{{ i18n['uuid'] }}</th>
                            <th>{{ i18n['status'] }}</th>
                            <th>{{ i18n['date'] }}</th>
                            <th>{{ i18n['time'] }}</th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr v-for="(item, index) in dataTracking" :key="index">
                            <td>{{ getSerialNumber(index) }}</td>
                            <td>{{ item.uuid }}</td>
                            <td>{{ item.content }}</td>
                            <td>{{ formatDate(item.created_at) }}</td>
                            <td>{{ formatTime(item.created_at) }}</td>
                        </tr>
                    </tbody>
                </v-simple-table>

                <!-- Pagination Controls -->
                <div class="d-flex justify-center mt-5">
                    <v-pagination v-model="currentPage" :length="lastPage" :total-visible="5" @input="changePage"
                        color="secondary" :disabled="loading"></v-pagination>
                </div>

                <!-- Pagination Info -->
                <div class="text-center mt-2 text-caption">
                    {{ i18n['showing'] || 'Showing' }} {{ paginationData.from }} {{ i18n['to'] || 'to' }} {{
                    paginationData.to }} {{ i18n['of'] || 'of' }} {{ paginationData.total }} {{ i18n['entries'] ||
                    'entries' }}
                </div>
            </v-col>
        </v-row>

        <v-row v-else-if="!loading && filters.uuid">
            <v-col cols="12" class="text-center">
                <v-alert type="info" border="left" colored-border>
                    {{ i18n["no_results_found"] || "No matching records found." }}
                </v-alert>
            </v-col>
        </v-row>
    </v-container>
</template>

<script>
import axios from 'axios';

export default {
    data() {
        return {
            loading: false,
            filters: {
                uuid: '',
                date: '',
            },
            dataTracking: [],
            currentPage: 1,
            lastPage: 1,
            paginationData: {
                current_page: 1,
                from: 0,
                to: 0,
                total: 0,
                per_page: 10
            }
        };
    },
    computed: {
        i18n() {
            return this.$store.state.i18n[this.$store.state.language];
        },
        arabic() {
            return this.$store.state.language === 'ar';
        },
    },
    methods: {
        localizedClass(baseClass = '') {
            return `${this.arabic ? 'arFont' : 'enFont'} ${baseClass}`;
        },

        async trackOrders() {
            this.currentPage = 1;
            await this.fetchData();
        },

        async fetchData() {
            const { uuid, date } = this.filters;

            this.loading = true;

            try {
                const queryParams = new URLSearchParams();

                if (uuid) {
                    queryParams.append('uuid', uuid);
                }

                if (date) {
                    const formattedDate = new Date(date).toISOString().split('T')[0];
                    queryParams.append('date', formattedDate);
                }

                queryParams.append('page', this.currentPage);

                const response = await this.$store.dispatch('getResource', {
                    resource: `/filter-by-uuid?${queryParams.toString()}`
                });

                // Handle paginated response
                if (response && typeof response === 'object') {
                    this.dataTracking = response.data || [];
                    this.lastPage = response.last_page || 1;
                    this.paginationData = {
                        current_page: response.current_page || 1,
                        from: response.from || 0,
                        to: response.to || 0,
                        total: response.total || 0,
                        per_page: response.per_page || 10
                    };
                } else {
                    this.dataTracking = Array.isArray(response) ? response : [];
                    this.lastPage = 1;
                    this.paginationData = {
                        current_page: 1,
                        from: this.dataTracking.length > 0 ? 1 : 0,
                        to: this.dataTracking.length,
                        total: this.dataTracking.length,
                        per_page: 10
                    };
                }

                if (this.dataTracking.length === 0) {
                    console.warn('No matching records found.');
                }
            } catch (error) {
                console.error('Error fetching data:', error);
                this.dataTracking = [];
                this.lastPage = 1;
                this.paginationData = {
                    current_page: 1,
                    from: 0,
                    to: 0,
                    total: 0,
                    per_page: 10
                };
            } finally {
                this.loading = false;
            }
        },

        async changePage(page) {
            this.currentPage = page;
            await this.fetchData();
        },

        getSerialNumber(index) {
            return ((this.paginationData.current_page - 1) * this.paginationData.per_page) + index + 1;
        },

        formatDate(dateString) {
            const date = new Date(dateString);
            return date.toISOString().split('T')[0];
        },

        formatTime(dateString) {
            const date = new Date(dateString);
            let hours = date.getHours();
            const minutes = date.getMinutes().toString().padStart(2, '0');
            const seconds = date.getSeconds().toString().padStart(2, '0');
            const ampm = hours >= 12 ? 'PM' : 'AM';
            hours = hours % 12 || 12;
            return `${hours}:${minutes}:${seconds} ${ampm}`;
        },

        async exportLeadUpdates() {
            try {
                const params = new URLSearchParams();
                if (this.filters.uuid) params.append('uuid', this.filters.uuid);
                if (this.filters.date) params.append('date', this.filters.date);

                const fullUrl = `https://yervip.com/api/v1/lead-updates/status?${params.toString()}`;

                const response = await axios.get(fullUrl, {
                    responseType: 'blob',
                });

                const url = window.URL.createObjectURL(new Blob([response.data]));
                const link = document.createElement('a');
                link.href = url;
                link.setAttribute('download', `lead-updates-${new Date().toISOString().split('T')[0]}.xlsx`);
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);

                console.log('Export finished successfully.');
            } catch (error) {
                console.error('Export error:', error);
                alert('Error exporting file.');
            }
        }





    }
};
</script>

<style></style>